import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Upload, Loader2, CheckCircle2, FileText, Building2, Phone, Briefcase, Share2, Image as ImageIcon } from 'lucide-react';
import { RegistrationWizard, ChipMultiSelect, ChipInput, WizardStep } from '@/components/onboarding/RegistrationWizard';

interface DocUpload { label: string; field: string; required: boolean; description?: string; }

const requiredDocs: DocUpload[] = [
  { label: 'Business License', field: 'trade_license_url', required: true },
  { label: 'Trade Registration', field: 'trade_name_registration_url', required: true },
  { label: 'TIN Certificate', field: 'tin_certificate_url', required: true },
  { label: 'VAT Certificate', field: 'vat_certificate_url', required: false, description: 'If applicable' },
];

const SERVICE_OPTIONS = ['Sales', 'Rentals', 'Property Management', 'Valuation', 'Consulting', 'Mortgage', 'Legal'];
const SPECIALIZATION_OPTIONS = ['Residential', 'Commercial', 'Land & Plots', 'New Projects', 'Luxury', 'Affordable Housing'];

export default function AgencyApplication() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Basic
  const [agencyName, setAgencyName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [establishedYear, setEstablishedYear] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [logoPath, setLogoPath] = useState('');
  const [logoPreview, setLogoPreview] = useState('');

  // Contact
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');

  // Profile
  const [description, setDescription] = useState('');

  // Services
  const [services, setServices] = useState<string[]>([]);
  const [specializations, setSpecializations] = useState<string[]>([]);
  const [areas, setAreas] = useState<string[]>([]);

  // Social
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialLinkedin, setSocialLinkedin] = useState('');
  const [socialTelegram, setSocialTelegram] = useState('');

  // Docs
  const [docs, setDocs] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  if (!user) { navigate('/login'); return null; }

  const uploadDoc = async (file: File, field: string, isLogo = false) => {
    if (!['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast({ variant: 'destructive', title: 'Invalid file', description: 'PDF, JPG, PNG, WEBP only' }); return;
    }
    if (file.size > 15 * 1024 * 1024) { toast({ variant: 'destructive', title: 'File too large', description: 'Max 15MB' }); return; }
    setUploading(p => ({ ...p, [field]: true }));
    const ext = file.name.split('.').pop() || 'pdf';
    const path = `${user.id}/${field}_${Date.now()}.${ext}`;
    if (isLogo) {
      const { error } = await supabase.storage.from('agency-logos').upload(path, file, { cacheControl: '3600', upsert: true });
      if (error) toast({ variant: 'destructive', title: 'Upload failed', description: error.message });
      else {
        const { data } = supabase.storage.from('agency-logos').getPublicUrl(path);
        setLogoPath(data.publicUrl);
        setLogoPreview(data.publicUrl);
      }
    } else {
      const { error } = await supabase.storage.from('developer-documents').upload(path, file, { cacheControl: '3600' });
      if (error) toast({ variant: 'destructive', title: 'Upload failed', description: error.message });
      else setDocs(p => ({ ...p, [field]: path }));
    }
    setUploading(p => ({ ...p, [field]: false }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const { data: insertedApp, error } = await supabase.from('agency_applications' as any).insert({
      user_id: user.id,
      agency_name: agencyName,
      manager_name: managerName,
      established_year: establishedYear ? Number(establishedYear) : null,
      employee_count: employeeCount ? Number(employeeCount) : null,
      logo_url: logoPath || null,
      phone, whatsapp: whatsapp || null, email, website: website || null,
      city: city || null, address: address || null,
      description: description || null,
      services_offered: services, specializations, areas_of_operation: areas,
      social_facebook: socialFacebook || null, social_instagram: socialInstagram || null,
      social_linkedin: socialLinkedin || null, social_telegram: socialTelegram || null,
      ...docs,
    }).select('id').single();
    setSubmitting(false);

    if (error) { toast({ variant: 'destructive', title: 'Submission failed', description: error.message }); return; }

    const { data: reviewers } = await supabase.from('user_roles').select('user_id')
      .in('role', ['superadmin', 'sales_manager', 'sales_team_leader', 'sales_agent'] as any);
    if (reviewers?.length) {
      await supabase.from('notifications').insert(reviewers.map((r: any) => ({
        user_id: r.user_id,
        title: 'New agency application',
        message: `${agencyName} submitted an agency application for review.`,
        type: 'application_submitted',
        metadata: { application_id: (insertedApp as any)?.id, application_type: 'agency' },
      })));
    }
    toast({ title: 'Application Submitted!', description: 'Our team will review and notify you when approved.' });
    navigate('/dashboard');
  };

  const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label} {required && <span className="text-destructive">*</span>}</Label>
      {children}
    </div>
  );

  const steps: WizardStep[] = [
    {
      id: 'basic', title: 'Agency Profile', shortTitle: 'Profile', icon: <Building2 className="h-5 w-5 text-primary" />,
      validate: () => !agencyName.trim() ? 'Agency name is required' : !managerName.trim() ? 'Manager name is required' : null,
      render: () => (
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <div className="h-24 w-24 rounded-lg border-2 border-dashed bg-muted/50 flex items-center justify-center overflow-hidden shrink-0">
              {logoPreview ? <img src={logoPreview} alt="logo" className="w-full h-full object-cover" /> : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
            </div>
            <div className="flex-1">
              <Label className="text-xs font-medium">Agency Logo</Label>
              <p className="text-xs text-muted-foreground mb-2">Square format works best (min 200x200)</p>
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20">
                <Upload className="h-3.5 w-3.5" /> {logoPath ? 'Change Logo' : 'Upload Logo'}
                <input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadDoc(f, 'logo_url', true); e.target.value = ''; }} />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Agency Name" required><Input value={agencyName} onChange={e => setAgencyName(e.target.value)} placeholder="e.g. Addis Premium Realty" /></Field>
            <Field label="Manager / Owner Name" required><Input value={managerName} onChange={e => setManagerName(e.target.value)} placeholder="Full name" /></Field>
            <Field label="Established Year"><Input type="number" value={establishedYear} onChange={e => setEstablishedYear(e.target.value)} placeholder="2015" /></Field>
            <Field label="Number of Agents / Staff"><Input type="number" value={employeeCount} onChange={e => setEmployeeCount(e.target.value)} placeholder="10" /></Field>
          </div>
        </div>
      ),
    },
    {
      id: 'contact', title: 'Contact Information', shortTitle: 'Contact', icon: <Phone className="h-5 w-5 text-primary" />,
      validate: () => !phone.trim() ? 'Phone is required' : !email.trim() ? 'Email is required' : !city.trim() ? 'City is required' : null,
      render: () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Phone" required><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+251..." /></Field>
          <Field label="WhatsApp"><Input value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="+251..." /></Field>
          <Field label="Email" required><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="hello@agency.com" /></Field>
          <Field label="Website"><Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." /></Field>
          <Field label="City" required><Input value={city} onChange={e => setCity(e.target.value)} placeholder="Addis Ababa" /></Field>
          <Field label="Office Address"><Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Bole, near Edna Mall" /></Field>
        </div>
      ),
    },
    {
      id: 'about', title: 'About Your Agency', shortTitle: 'About', icon: <FileText className="h-5 w-5 text-primary" />,
      render: () => (
        <Field label="Description / Background">
          <Textarea value={description} onChange={e => setDescription(e.target.value.slice(0, 1500))} rows={6}
            placeholder="Tell buyers and renters about your agency — years in business, key projects, what makes you different…" />
          <div className="text-xs text-muted-foreground text-right mt-1">{description.length}/1500</div>
        </Field>
      ),
    },
    {
      id: 'services', title: 'Services & Areas', shortTitle: 'Services', icon: <Briefcase className="h-5 w-5 text-primary" />,
      render: () => (
        <div className="space-y-6">
          <div>
            <Label className="text-xs font-medium">Services Offered</Label>
            <p className="text-xs text-muted-foreground mb-3">Select all that apply</p>
            <ChipMultiSelect options={SERVICE_OPTIONS} value={services} onChange={setServices} />
          </div>
          <div>
            <Label className="text-xs font-medium">Specializations</Label>
            <p className="text-xs text-muted-foreground mb-3">Property categories you focus on</p>
            <ChipMultiSelect options={SPECIALIZATION_OPTIONS} value={specializations} onChange={setSpecializations} />
          </div>
          <div>
            <Label className="text-xs font-medium">Areas of Operation</Label>
            <p className="text-xs text-muted-foreground mb-3">Neighborhoods, sub-cities you cover. Press Enter after each.</p>
            <ChipInput value={areas} onChange={setAreas} placeholder="e.g. Bole, CMC, Sarbet" />
          </div>
        </div>
      ),
    },
    {
      id: 'social', title: 'Social Media (Optional)', shortTitle: 'Social', icon: <Share2 className="h-5 w-5 text-primary" />,
      render: () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Facebook"><Input value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} placeholder="https://facebook.com/..." /></Field>
          <Field label="Instagram"><Input value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} placeholder="https://instagram.com/..." /></Field>
          <Field label="LinkedIn"><Input value={socialLinkedin} onChange={e => setSocialLinkedin(e.target.value)} placeholder="https://linkedin.com/..." /></Field>
          <Field label="Telegram"><Input value={socialTelegram} onChange={e => setSocialTelegram(e.target.value)} placeholder="https://t.me/..." /></Field>
        </div>
      ),
    },
    {
      id: 'docs', title: 'Verification Documents', shortTitle: 'Documents', icon: <FileText className="h-5 w-5 text-primary" />,
      validate: () => {
        const missing = requiredDocs.filter(d => d.required && !docs[d.field]);
        return missing.length > 0 ? `${missing.length} required document(s) remaining` : null;
      },
      render: () => (
        <div className="space-y-3">
          {requiredDocs.map(doc => (
            <div key={doc.field} className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{doc.label} {doc.required && <span className="text-destructive">*</span>}</div>
                {doc.description && <div className="text-xs text-muted-foreground">{doc.description}</div>}
              </div>
              {docs[doc.field] ? (
                <div className="flex items-center gap-2 text-sm text-green-600"><CheckCircle2 className="h-4 w-4" />Uploaded</div>
              ) : uploading[doc.field] ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <label className="cursor-pointer">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20"><Upload className="h-3.5 w-3.5" /> Upload</div>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadDoc(f, doc.field); e.target.value = ''; }} />
                </label>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'review', title: 'Review & Submit', shortTitle: 'Review', icon: <CheckCircle2 className="h-5 w-5 text-primary" />,
      render: () => (
        <div className="space-y-4 text-sm">
          <ReviewRow label="Agency" value={agencyName} />
          <ReviewRow label="Manager" value={managerName} />
          <ReviewRow label="Established" value={establishedYear} />
          <ReviewRow label="Staff" value={employeeCount} />
          <ReviewRow label="Phone" value={phone} />
          <ReviewRow label="WhatsApp" value={whatsapp} />
          <ReviewRow label="Email" value={email} />
          <ReviewRow label="City" value={city} />
          <ReviewRow label="Address" value={address} />
          <ReviewRow label="Website" value={website} />
          <ReviewRow label="Services" value={services.join(', ')} />
          <ReviewRow label="Specializations" value={specializations.join(', ')} />
          <ReviewRow label="Areas" value={areas.join(', ')} />
          <ReviewRow label="Documents" value={`${Object.keys(docs).length} uploaded`} />
          <p className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
            By submitting, you confirm that all information provided is accurate. Our team typically reviews applications within 1-3 business days.
          </p>
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <RegistrationWizard
        steps={steps}
        onSubmit={handleSubmit}
        submitting={submitting}
        title="Agency Registration"
        subtitle="List your agency on Ethio Real Estates and get discovered by thousands of property seekers."
        topIcon={<Building2 className="h-12 w-12 mx-auto text-primary" />}
      />
    </Layout>
  );
}

function ReviewRow({ label, value }: { label: string; value?: string | number | null }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right break-words max-w-[60%]">{value}</span>
    </div>
  );
}
